import type { AdminRuntimeConfig, SystemOperatorSessionPayload } from "@searchgrid/shared";
import type { AdminControlPlaneInfo } from "@searchgrid/shared";

declare global {
  interface Window {
    __adminStatus?: {
      api?: { port?: number };
      env?: "local" | "cloudflare";
    };
    __adminConfig?: Partial<AdminRuntimeConfig>;
  }
}

const OPERATOR_SESSION_STORAGE_KEY = "searchgrid-admin-operator-session";
const TARGET_PROFILE_STORAGE_KEY = "searchgrid-admin-target-profile";
const CUSTOM_TARGET_PROFILES_STORAGE_KEY = "searchgrid-admin-custom-target-profiles";
let inMemoryOperatorSession: SystemOperatorSessionPayload | null = null;

export interface AdminTargetProfile {
  id: string;
  label: string;
  controlPlaneBaseUrl: string;
  managedAppBaseUrl: string;
  webAppBaseUrl: string;
  controlPlane?: AdminControlPlaneInfo;
  builtin?: boolean;
}

const DEFAULT_LOCAL_TARGET_PROFILES: AdminTargetProfile[] = [
  {
    id: "local-vps",
    label: "Local VPS",
    controlPlaneBaseUrl: "",
    managedAppBaseUrl: "http://127.0.0.1:8787/api/v1",
    webAppBaseUrl: "http://localhost:5173",
    controlPlane: {
      target: "vps",
      mode: "local",
      authMode: "password",
      authRequired: true,
      supportsDirectDbAccess: true,
      supportsProcessControl: true,
      supportsAuditLog: true,
    },
    builtin: true,
  },
  {
    id: "local-cloudflare",
    label: "Local Cloudflare",
    controlPlaneBaseUrl: "http://127.0.0.1:8787/api/v1/control-plane",
    managedAppBaseUrl: "http://127.0.0.1:8787/api/v1",
    webAppBaseUrl: "http://127.0.0.1:8788",
    controlPlane: {
      target: "cloudflare",
      mode: "local",
      authMode: "password",
      authRequired: true,
      supportsDirectDbAccess: true,
      supportsProcessControl: false,
      supportsAuditLog: true,
    },
    builtin: true,
  },
];

const DEFAULT_REMOTE_TARGET_PROFILES: AdminTargetProfile[] = [
  {
    id: "lby-cloudflare",
    label: "LBY Cloudflare",
    controlPlaneBaseUrl: "https://api.grid.lby.app/api/v1/control-plane",
    managedAppBaseUrl: "https://api.grid.lby.app/api/v1",
    webAppBaseUrl: "https://web.grid.lby.app",
    controlPlane: {
      target: "cloudflare",
      mode: "production",
      authMode: "password",
      authRequired: true,
      supportsDirectDbAccess: true,
      supportsProcessControl: false,
      supportsAuditLog: true,
    },
    builtin: true,
  },
];

function trimTrailingSlash(value: string) {
  return value.replace(/\/+$/, "");
}

export function isLocalDashboardHost() {
  const host = window.location.hostname;
  return host === "127.0.0.1" || host === "localhost" || host === "::1";
}

function makeInjectedProfile(): AdminTargetProfile | null {
  const controlPlane = window.__adminConfig?.controlPlane;
  const controlPlaneBaseUrl = window.__adminConfig?.controlPlaneBaseUrl;
  const managedAppBaseUrl = window.__adminConfig?.managedAppBaseUrl;
  const webAppBaseUrl = window.__adminConfig?.webAppBaseUrl;
  if (!controlPlaneBaseUrl || !managedAppBaseUrl) return null;
  const target = controlPlane?.target ?? "vps";
  const mode = controlPlane?.mode ?? "production";
  const label = `${target === "cloudflare" ? "Cloudflare" : "VPS"} ${mode[0].toUpperCase()}${mode.slice(1)}`;
  return {
    id: "current-remote",
    label,
    controlPlaneBaseUrl: trimTrailingSlash(controlPlaneBaseUrl),
    managedAppBaseUrl: trimTrailingSlash(managedAppBaseUrl),
    webAppBaseUrl: trimTrailingSlash(webAppBaseUrl ?? window.location.origin),
    controlPlane,
    builtin: true,
  };
}

function makeLocalProfile(): AdminTargetProfile {
  const apiPort = window.__adminStatus?.api?.port ?? 8787;
  const controlPlane = window.location.origin.replace(/\/+$/, "");
  return {
    ...DEFAULT_LOCAL_TARGET_PROFILES[0],
    controlPlaneBaseUrl: `${controlPlane}/api`,
    managedAppBaseUrl: `http://127.0.0.1:${apiPort}/api/v1`,
  };
}

export function getCustomTargetProfiles(): AdminTargetProfile[] {
  try {
    const raw = window.localStorage.getItem(CUSTOM_TARGET_PROFILES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as AdminTargetProfile[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveCustomTargetProfiles(profiles: AdminTargetProfile[]) {
  window.localStorage.setItem(CUSTOM_TARGET_PROFILES_STORAGE_KEY, JSON.stringify(profiles));
}

export function getAvailableTargetProfiles(): AdminTargetProfile[] {
  const profiles: AdminTargetProfile[] = [];
  if (isLocalDashboardHost()) {
    profiles.push(makeLocalProfile());
    profiles.push(DEFAULT_LOCAL_TARGET_PROFILES[1]);
    profiles.push(...DEFAULT_REMOTE_TARGET_PROFILES);
  }
  const injected = makeInjectedProfile();
  if (injected) {
    if (!profiles.some((existing) => existing.controlPlaneBaseUrl === injected.controlPlaneBaseUrl)) {
      profiles.push(injected);
    }
  }
  for (const profile of getCustomTargetProfiles()) {
    if (!profiles.some((existing) => existing.id === profile.id)) {
      profiles.push(profile);
    }
  }
  return profiles;
}

export function getSelectedTargetProfileId() {
  const selected = window.localStorage.getItem(TARGET_PROFILE_STORAGE_KEY);
  if (selected && getAvailableTargetProfiles().some((profile) => profile.id === selected)) {
    return selected;
  }
  return isLocalDashboardHost() ? "local-vps" : (makeInjectedProfile()?.id ?? "default");
}

export function setSelectedTargetProfileId(profileId: string) {
  window.localStorage.setItem(TARGET_PROFILE_STORAGE_KEY, profileId);
}

export function getSelectedTargetProfile(): AdminTargetProfile | null {
  const selectedId = getSelectedTargetProfileId();
  return getAvailableTargetProfiles().find((profile) => profile.id === selectedId) ?? null;
}

function resolveControlPlaneBaseUrl() {
  const selected = getSelectedTargetProfile();
  if (selected) {
    return trimTrailingSlash(selected.controlPlaneBaseUrl);
  }
  const injected = window.__adminConfig?.controlPlaneBaseUrl;
  if (typeof injected === "string" && injected.length > 0) {
    return trimTrailingSlash(injected);
  }
  return "/api";
}

function resolveManagedAppBaseUrl() {
  const selected = getSelectedTargetProfile();
  if (selected) {
    return trimTrailingSlash(selected.managedAppBaseUrl);
  }
  const injected = window.__adminConfig?.managedAppBaseUrl;
  if (typeof injected === "string" && injected.length > 0) {
    return trimTrailingSlash(injected);
  }

  const port = window.__adminStatus?.api?.port ?? 8787;
  return `http://127.0.0.1:${port}/api/v1`;
}

function resolveManagedWebBaseUrl() {
  const selected = getSelectedTargetProfile();
  if (selected) {
    return trimTrailingSlash(selected.webAppBaseUrl);
  }
  const injected = window.__adminConfig?.webAppBaseUrl;
  if (typeof injected === "string" && injected.length > 0) {
    return trimTrailingSlash(injected);
  }

  const controlPlane = window.__adminConfig?.controlPlane;
  if (controlPlane?.target === "cloudflare" && controlPlane.mode === "local") {
    return "http://127.0.0.1:8788";
  }

  const webPort = 5173;
  return `http://localhost:${webPort}`;
}

export function getAdminRuntimeConfig(): AdminRuntimeConfig {
  const selected = getSelectedTargetProfile();
  return {
    controlPlaneBaseUrl: resolveControlPlaneBaseUrl(),
    managedAppBaseUrl: resolveManagedAppBaseUrl(),
    webAppBaseUrl: resolveManagedWebBaseUrl(),
    controlPlane: selected?.controlPlane ?? window.__adminConfig?.controlPlane ?? {
      target: "vps",
      mode: "local",
      authMode: "none",
      authRequired: false,
      supportsDirectDbAccess: true,
      supportsProcessControl: true,
      supportsAuditLog: true,
    },
  };
}

function operatorSessionStorageKey() {
  return `${OPERATOR_SESSION_STORAGE_KEY}:${getSelectedTargetProfileId()}`;
}

export function getOperatorSession(): SystemOperatorSessionPayload | null {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(operatorSessionStorageKey()) ?? window.localStorage.getItem(OPERATOR_SESSION_STORAGE_KEY);
  } catch {
    return inMemoryOperatorSession;
  }
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SystemOperatorSessionPayload;
  } catch {
    inMemoryOperatorSession = null;
    return null;
  }
}

export function setOperatorSession(session: SystemOperatorSessionPayload) {
  inMemoryOperatorSession = session;
  try {
    window.localStorage.setItem(operatorSessionStorageKey(), JSON.stringify(session));
  } catch {}
}

export function clearOperatorSession() {
  inMemoryOperatorSession = null;
  try {
    window.localStorage.removeItem(operatorSessionStorageKey());
  } catch {}
}

export function getOperatorToken() {
  return getOperatorSession()?.token ?? "";
}

export function isOperatorSessionExpired(session: SystemOperatorSessionPayload | null) {
  if (!session) return true;
  return Date.parse(session.expiresAt) <= Date.now();
}

export function buildControlPlaneHeaders() {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  const token = getOperatorToken().trim();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

export function adminControlPlaneUrl(path: string) {
  return `${getAdminRuntimeConfig().controlPlaneBaseUrl}${path}`;
}

export function adminControlPlaneWebSocketUrl(path: string) {
  const url = new URL(adminControlPlaneUrl(path), window.location.origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

export function managedAppUrl(path: string) {
  return `${getAdminRuntimeConfig().managedAppBaseUrl}${path}`;
}

export function managedWebUrl(path: string) {
  return `${getAdminRuntimeConfig().webAppBaseUrl ?? resolveManagedWebBaseUrl()}${path}`;
}

export function managedProjectUrl(projectRef: string) {
  return managedWebUrl(`/projects/${projectRef}`);
}

export function managedSearcherUrl(projectRef: string) {
  return managedWebUrl(`/search/${projectRef}`);
}

export function managedJoinUrl(token: string) {
  return managedWebUrl(`/join/${token}`);
}

export function managedOrgLoginUrl(orgSlug: string, token: string) {
  return managedWebUrl(`/org/${orgSlug}/login/${token}`);
}
